import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const Layout: React.FC = () => {
  const [apiHealth, setApiHealth] = useState<'loading' | 'healthy' | 'error'>('loading');
  const [showAdminTabs, setShowAdminTabs] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    checkApiHealth();
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu && !(event.target as Element).closest('.user-menu')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const checkApiHealth = async () => {
    try {
      const response = await fetch('/api/health');
      if (response.ok) {
        setApiHealth('healthy');
      } else {
        setApiHealth('error');
      }
    } catch (error) {
      setApiHealth('error');
    }
  };

  const navItems = [
    { path: '/', name: 'GoalYet™', icon: '🎯' },
    { path: '/assistant', name: 'AI Assistant', icon: '🤖' },
    { path: '/goals', name: 'Goal Tracker', icon: '📊' },
    { path: '/goals-history', name: 'History', icon: '📜' },
    { path: '/checklists', name: 'Checklists', icon: '✅' },
    { path: '/dashboard', name: 'Overview', icon: '🏠' }
  ];

  const adminItems = [
    { path: '/projects', name: 'Projects', icon: '🚀' },
    { path: '/users', name: 'Users', icon: '👥' },
    { path: '/files', name: 'Files', icon: '📁' },
    { path: '/build', name: 'Build', icon: '🏗️' },
    { path: '/api', name: 'API', icon: '🔌' },
    { path: '/debug', name: 'Debug', icon: '🔧' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-teal-50">
      {/* Minimal Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">🎯</div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-teal-600 bg-clip-text text-transparent">GoalYet</h1>
            </div>

            {/* User Menu Toggle */}
            <div className="relative user-menu">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {user && (
                    <>
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user.email}</p>
                        <div className={`inline-flex items-center space-x-2 px-2 py-1 rounded-full text-xs mt-2 ${
                          apiHealth === 'healthy' ? 'bg-green-100 text-green-800' :
                          apiHealth === 'error' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${
                            apiHealth === 'healthy' ? 'bg-green-500' :
                            apiHealth === 'error' ? 'bg-red-500' :
                            'bg-yellow-500'
                          }`} />
                          <span>API {apiHealth === 'healthy' ? 'Online' : apiHealth === 'error' ? 'Offline' : 'Checking'}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setShowAdminTabs(!showAdminTabs);
                          setShowUserMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center space-x-2 ${
                          showAdminTabs ? 'text-teal-700' : 'text-gray-700'
                        }`}
                      >
                        <span>{showAdminTabs ? '🔧' : '⚙️'}</span>
                        <span>{showAdminTabs ? 'Hide Admin Tabs' : 'Show Admin Tabs'}</span>
                      </button>

                      <button
                        onClick={() => {
                          logout();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2"
                      >
                        <span>🚪</span>
                        <span>Logout</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white/60 backdrop-blur-sm border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${isActive
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-teal-300'
                  }
                `}
              >
                <span>{item.icon}</span>
                <span>{item.name}</span>
              </NavLink>
            ))}

            {showAdminTabs && (
              <>
                <div className="border-l border-gray-200 h-8 my-4"></div>
                {adminItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) => `
                      flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${isActive
                        ? 'border-teal-500 text-teal-600'
                        : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
                      }
                    `}
                  >
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </NavLink>
                ))}
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
};